import { useEffect, useRef } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { useAuth } from "@/lib/AuthContext";
import { useAcceptInvite } from "@/lib/queries";
import styles from "./JoinWorkspace.module.css";

/**
 * JoinWorkspace — the invite-accept landing page (`/w/:ws/join/:token`).
 *
 * Copy-link invites are existing-accounts-only and email-bound: an admin
 * mints a link and shares it, and the recipient opens it here. If they're
 * not signed in we bounce to /signin with a ?next= back to this page; once
 * authenticated we POST the accept exactly once and show the result.
 *
 * This page is intentionally NOT wrapped in the AppShell side-nav — the
 * visitor may not be a workspace member yet, so a centered standalone card
 * is the right surface. On success we point them into the workspace.
 */

/** Pull a human-readable message off a thrown mutation error. */
function errMessage(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) {
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  return "This invite link is invalid, expired, or already used.";
}

export function JoinWorkspace() {
  const { ws, token } = useParams<{ ws: string; token: string }>();
  const location = useLocation();
  const auth = useAuth();
  const acceptInvite = useAcceptInvite(ws);

  // Guard so the accept fires exactly once — React strict-mode double-renders
  // (and any re-render) must not re-POST the accept. We do NOT auto-retry on
  // error either; a failed accept stays failed until the user opens a fresh
  // link.
  const startedRef = useRef(false);

  useEffect(() => {
    if (auth.status !== "authenticated") return;
    if (!ws || !token) return;
    if (startedRef.current) return;
    startedRef.current = true;
    acceptInvite.mutate({ token });
  }, [auth.status, ws, token, acceptInvite]);

  // Still resolving the session — hold before deciding to redirect or accept.
  if (auth.status === "loading") {
    return (
      <main className={styles.page}>
        <div className={styles.card}>
          <p className={styles.lede}>Loading…</p>
        </div>
      </main>
    );
  }

  // Not signed in — send to sign-in and come straight back here after.
  if (auth.status === "anonymous") {
    return (
      <Navigate
        to={`/signin?next=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  // Missing route params (shouldn't happen via the router, but be explicit).
  if (!ws || !token) {
    return (
      <main className={styles.page}>
        <div className={styles.card}>
          <div className={`${styles.iconBadge} ${styles.iconBadgeError}`}>
            <Icon name="alert" size={28} />
          </div>
          <h1 className={styles.title}>Invalid invite link</h1>
          <p className={styles.lede}>This link is missing information.</p>
          <Btn href="/app" variant="secondary" iconLeft={<Icon name="home" size={14} />}>
            Go to your workspaces
          </Btn>
        </div>
      </main>
    );
  }

  const result = acceptInvite.data;

  // Error — surface the server message (expired / wrong-email / used). No retry.
  if (acceptInvite.isError) {
    return (
      <main className={styles.page}>
        <div className={styles.card}>
          <div className={`${styles.iconBadge} ${styles.iconBadgeError}`}>
            <Icon name="x-circle" size={28} />
          </div>
          <h1 className={styles.title}>Couldn’t join</h1>
          <p className={styles.lede}>{errMessage(acceptInvite.error)}</p>
          <p className={styles.muted}>
            Invite links are bound to a specific email and expire after 7 days.
            Ask an admin for a fresh link if yours no longer works.
          </p>
          <Btn href="/app" variant="secondary" iconLeft={<Icon name="home" size={14} />}>
            Go to your workspaces
          </Btn>
        </div>
      </main>
    );
  }

  // Success — already a member.
  if (result?.alreadyMember) {
    return (
      <main className={styles.page}>
        <div className={styles.card}>
          <div className={`${styles.iconBadge} ${styles.iconBadgeOk}`}>
            <Icon name="check-circle" size={28} />
          </div>
          <h1 className={styles.title}>You’re already a member</h1>
          <p className={styles.lede}>
            You already have access to this workspace.
          </p>
          <Btn href={`/w/${ws}`} variant="primary" iconRight={<Icon name="arrow-right" size={14} />}>
            Open workspace
          </Btn>
        </div>
      </main>
    );
  }

  // Success — just joined.
  if (acceptInvite.isSuccess) {
    return (
      <main className={styles.page}>
        <div className={styles.card}>
          <div className={`${styles.iconBadge} ${styles.iconBadgeOk}`}>
            <Icon name="check-circle" size={28} />
          </div>
          <h1 className={styles.title}>You’re in!</h1>
          <p className={styles.lede}>You’ve joined the workspace.</p>
          <Btn href={`/w/${ws}`} variant="primary" iconRight={<Icon name="arrow-right" size={14} />}>
            Open workspace
          </Btn>
        </div>
      </main>
    );
  }

  // Pending (or about to fire) — joining spinner.
  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <span className={styles.spinner} aria-hidden="true" />
        <h1 className={styles.title}>Joining workspace…</h1>
        <p className={styles.lede}>Hang tight while we add you.</p>
      </div>
    </main>
  );
}
